import React, { FC, useContext } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { clientLog } from 'common/utils/helpers';
import { selectCardElements } from 'client/reducers/card/selectors';
import { selectEditorState } from 'client/reducers/editor/selectors';
import { TRAVERSE_TREE_TARGETS } from 'client/components/common/BuildingBlocks/utils/common';
import { traverseTree } from 'client/components/common/BuildingBlocks/utils/traverse-tree';
import { Context as ResponsiveProviderContext } from 'client/components/common/ResponsiveProvider/Context';
import { BBModel, StorySymbols } from 'types/story';
import { ClientReducerState } from 'client/reducers';
import {
	ITraverseTreeOptions,
	ITraverseTreeOptionsListeners,
} from 'client/components/common/BuildingBlocks/utils/traverse-tree-types';

const log = clientLog.extend('TreeRenderer');

const mapStateToProps = (state: ClientReducerState) => ({
	editorMode: state.editor.editorMode,
	state: selectEditorState(state),
	cardElements: selectCardElements(state)!,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector> & {
	treeOptions:
		| { isCardTree: ITraverseTreeOptions['isCardTree']; target: ITraverseTreeOptions['target'] }
		| ({ isCardTree: ITraverseTreeOptions['isCardTree']; target: 'editor' } & ITraverseTreeOptionsListeners);
	elements: BBModel[];
	symbols?: StorySymbols;
};

type ExtraProps = {
	state?: Props['state'];
};

const TreeRenderer: FC<Props> = props => {
	const isEditor = props.treeOptions.target === TRAVERSE_TREE_TARGETS.EDITOR;
	const extraProps: ExtraProps = isEditor ? { state: props.state } : {};
	const { mediaQuery, currentMediaQuery } = useContext(ResponsiveProviderContext);

	log(`%c${props.treeOptions.isCardTree ? 'cardElements' : 'storyElements'}`, 'font-weight: bold');

	if (!mediaQuery || !currentMediaQuery) {
		return null;
	}

	try {
		return traverseTree({
			tree: props.elements,
			options: {
				...props.treeOptions,
				...extraProps,
				mediaQuery,
				currentMediaQuery,
				cardElements: props.cardElements,
				symbols: props.symbols,
				editorMode: props.editorMode,
			},
		});
	} catch (e) {
		console.error(e);
		if (isEditor) {
			// FIXME
			const l = window?.parent?.document?.documentElement?.querySelector('[class^="editor-loading--"]');
			if (l) {
				if (l.previousSibling) (l.previousSibling as HTMLElement).style.height = '100vh';
				l.remove();
			}
		}
		return null;
	}
};

function areEqual(prevProps: Props, nextProps: Props) {
	const isEditorModeEqual = prevProps.editorMode === nextProps.editorMode;
	const isElementsEqual = prevProps.elements === nextProps.elements;
	const isSymbolsEqual = prevProps.symbols === nextProps.symbols;
	const isStateEqual =
		Object.keys(nextProps.state.source).some(key => nextProps.state.source[key] === prevProps.state.source[key]) &&
		prevProps.state.state === nextProps.state.state;

	const isEqual = isEditorModeEqual && isStateEqual && isElementsEqual && isSymbolsEqual;

	if (!nextProps.treeOptions.isCardTree) {
		const isCardElementsEqual = prevProps.cardElements === nextProps.cardElements;
		return isEqual && isCardElementsEqual;
	}

	return isEqual;
}

export default connector(React.memo(TreeRenderer, areEqual));
